import {
  Activity,
  AlertCircle,
  ArrowRight,
  ArrowRightLeft,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  FileText,
  MessageSquare,
  UserPlus,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RepaymentTracker } from '@/components/dashboard/RepaymentTracker';
import { PageLayout } from '@/components/layout/PageLayout';
import { Spinner } from '@/components/ui';
import { ComponentErrorBoundary, DataTable, StatCard } from '@/components/ui';
import { Button } from '@/components/ui';
import { useGlobalActivity } from '@/hooks/use-activity';
import { useApprovalStats } from '@/hooks/use-approvals';
import {
  useArrearsAging,
  useDiaryOverdue,
  useFundUtilisation,
  useUpcomingMaturities,
} from '@/hooks/use-enhanced-dashboard';
import { useRecentLoans } from '@/hooks/use-recent-loans';
import {
  useDelinquencyReport,
  useMonthlyOrigination,
  usePipelineReport,
  usePortfolioSummary,
  useStatusDistribution,
  useTopBorrowers,
  useTypeBreakdown,
} from '@/hooks/use-reports';
import { cn } from '@/lib/cn';
import { fmtCurrency as fmt, fmtPercent, fmtTimeAgo } from '@/lib/format';
import { LOAN_STATUS_CONFIG, type LoanStatus } from '@/types';

// ── Formatters ────────────────────────────────────────

function fmtPct(num: number, decimals = 1): string {
  return `${(num * 100).toFixed(decimals)}%`;
}

function fmtRate(num: number): string {
  return fmtPercent(num, 2);
}

// ── Mini Bar Chart (pure CSS) ────────────────────────

function MiniBarChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1.5 h-32">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] font-medium text-gray-500">{d.value}</span>
          <div
            className="w-full rounded-t-sm transition-all min-h-[4px]"
            style={{
              height: `${(d.value / max) * 100}%`,
              backgroundColor: d.color,
            }}
          />
          <span className="text-[9px] text-gray-400 truncate w-full text-center">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Origination Trend (CSS area-like chart) ──────────

function OriginationChart({ data }: { data: { month: string; count: number; amount: number }[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-8">No origination data yet</p>;
  }

  const maxAmt = Math.max(...data.map(d => d.amount), 1);

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-1 h-36">
        {data.map((d, i) => {
          const pct = (d.amount / maxAmt) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {fmt(d.amount)} · {d.count} loans
              </div>
              <div
                className="w-full bg-indigo-500 rounded-t-sm transition-all hover:bg-indigo-600 min-h-[4px]"
                style={{ height: `${Math.max(pct, 3)}%` }}
              />
              <span className="text-[9px] text-gray-400">{d.month.split('-')[1]}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-400">
        <span>{data[0]?.month}</span>
        <span>{data[data.length - 1]?.month}</span>
      </div>
    </div>
  );
}

// ── Delinquency Breakdown ─────────────────────────────

function DelinquencyBreakdown({
  buckets,
  totalAtRisk,
  totalAtRiskAmount,
}: {
  buckets: Record<string, { count: number; amount: number }>;
  totalAtRisk: number;
  totalAtRiskAmount: number;
}) {
  const rows = [
    { key: 'DELINQUENT_30', label: '30 Days', color: '#f59e0b' },
    { key: 'DELINQUENT_60', label: '60 Days', color: '#f97316' },
    { key: 'DELINQUENT_90', label: '90 Days', color: '#ef4444' },
    { key: 'DEFAULT', label: 'Default', color: '#991b1b' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {totalAtRisk} loan{totalAtRisk !== 1 ? 's' : ''} at risk
        </p>
        <p className="text-xs font-medium text-red-600">{fmt(totalAtRiskAmount)}</p>
      </div>

      {/* Stacked horizontal bar */}
      {totalAtRiskAmount > 0 && (
        <div className="flex h-6 rounded-md overflow-hidden">
          {rows.map(r => {
            const bucket = buckets[r.key];
            if (!bucket || bucket.amount === 0) return null;
            const pct = (bucket.amount / totalAtRiskAmount) * 100;
            return (
              <div
                key={r.key}
                className="flex items-center justify-center transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: r.color,
                  minWidth: bucket.amount > 0 ? '20px' : 0,
                }}
                title={`${r.label}: ${fmt(bucket.amount)} (${bucket.count})`}
              >
                {pct > 15 && (
                  <span className="text-[9px] font-semibold text-white">{bucket.count}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-1.5">
        {rows.map(r => {
          const bucket = buckets[r.key] ?? { count: 0, amount: 0 };
          return (
            <div key={r.key} className="flex items-center gap-2 text-xs">
              <div
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: r.color }}
              />
              <span className="text-gray-600 w-16">{r.label}</span>
              <span className="text-gray-800 font-medium">{bucket.count}</span>
              <span className="text-gray-400 ml-auto">{fmt(bucket.amount)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────

export function Dashboard() {
  const navigate = useNavigate();
  const [showAllBorrowers, setShowAllBorrowers] = useState(false);

  const { data: summary, isLoading: summaryLoading } = usePortfolioSummary();
  const { data: statusDist } = useStatusDistribution();
  const { data: delinquency } = useDelinquencyReport();
  const { data: topBorrowers } = useTopBorrowers(10);
  const { data: origination } = useMonthlyOrigination(12);
  const { data: typeBreakdown } = useTypeBreakdown();
  const { data: pipeline } = usePipelineReport();
  const { data: recentActivity } = useGlobalActivity({ limit: 8 });
  const { recent: recentLoans } = useRecentLoans();
  const { data: fundUtilisation } = useFundUtilisation();
  const { data: upcomingMaturities } = useUpcomingMaturities(90);
  const { data: arrearsAging } = useArrearsAging();
  const { data: diaryOverdue } = useDiaryOverdue();
  const { data: approvalStats } = useApprovalStats();

  // Status distribution chart data (memoized to prevent re-computation on parent re-render)
  const statusChartData = useMemo(
    () =>
      (statusDist ?? [])
        .filter(s => s.count > 0)
        .map(s => ({
          label: LOAN_STATUS_CONFIG[s.status as LoanStatus]?.label ?? s.status,
          value: s.count,
          color: LOAN_STATUS_CONFIG[s.status as LoanStatus]?.color ?? '#94a3b8',
        })),
    [statusDist],
  );

  // Type breakdown chart data (memoized to prevent re-computation)
  const typeChartData = useMemo(
    () =>
      (typeBreakdown ?? []).map(t => ({
        label: t.loanType.replace(/_/g, ' ').slice(0, 8),
        value: t.count,
        color:
          {
            PERSONAL: '#6366f1',
            BUSINESS: '#0ea5e9',
            MORTGAGE: '#10b981',
            AUTO: '#f59e0b',
            LINE_OF_CREDIT: '#8b5cf6',
          }[t.loanType] ?? '#94a3b8',
      })),
    [typeBreakdown],
  );

  // Displayed borrowers list (memoized to prevent slice re-computation)
  const displayedBorrowers = useMemo(
    () => (showAllBorrowers ? (topBorrowers ?? []) : (topBorrowers ?? []).slice(0, 5)),
    [showAllBorrowers, topBorrowers],
  );

  if (summaryLoading || !summary) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading dashboard…
      </div>
    );
  }

  return (
    <PageLayout
      title="Dashboard"
      headerBottomContent={
        <p className="text-xs text-gray-500">
          {new Date().toLocaleDateString('en-AU', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
      }
      headerAction={
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={() => {
              navigate('/loans');
              setTimeout(() => window.dispatchEvent(new CustomEvent('app:new-loan')), 100);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            New Loan
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              navigate('/borrowers');
              setTimeout(() => window.dispatchEvent(new CustomEvent('app:new-borrower')), 100);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors !w-auto !min-w-[124px] !min-h-[36px] shrink-0"
          >
            <Users className="w-3.5 h-3.5" />
            New Borrower
          </Button>
        </div>
      }
      contentClassName="space-y-6"
    >
      {/* KPI cards — row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total Loans"
          value={String(summary.totalLoans)}
          subtitle={`${fmt(summary.totalPrincipal)} total principal`}
        />
        <StatCard
          label="Active Loans"
          value={String(summary.activeLoans)}
          subtitle={`${fmt(summary.activePrincipal)} outstanding`}
        />
        <StatCard
          label="Pipeline"
          value={fmt(summary.pipelinePrincipal)}
          subtitle={`${summary.pipelineLoans} loan${summary.pipelineLoans !== 1 ? 's' : ''} in progress`}
        />
        <StatCard
          label="Delinquent"
          value={String(summary.delinquentLoans)}
          subtitle={
            summary.delinquentLoans > 0
              ? `${fmt(summary.delinquentPrincipal)} at risk`
              : 'All clear'
          }
        />
        <StatCard
          label="Paid Off"
          value={String(summary.paidOffLoans)}
          subtitle={fmt(summary.paidOffPrincipal)}
        />
      </div>

      {/* KPI cards — row 2 (rates & metrics) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Weighted Avg Rate"
          value={fmtRate(summary.weightedAvgRate)}
          subtitle={`Simple avg: ${fmtRate(summary.avgRate)}`}
        />
        <StatCard label="Avg Loan Size" value={fmt(summary.avgPrincipal)} />
        <StatCard label="Avg Term" value={`${Math.round(summary.avgTermMonths)} months`} />
        <StatCard
          label="Delinquency Rate"
          value={fmtPct(summary.delinquencyRate)}
          subtitle={summary.delinquencyRate > 0.1 ? 'Above threshold' : 'Within target'}
        />
      </div>

      {/* Fund Utilisation */}
      {fundUtilisation && fundUtilisation.length > 0 && (
        <ComponentErrorBoundary name="Fund Utilisation">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Fund Utilisation</h2>
            <div className="space-y-4">
              {fundUtilisation.map((fund: any) => {
                const utilPct =
                  fund.capitalCommitted > 0
                    ? (fund.capitalDeployed / fund.capitalCommitted) * 100
                    : 0;
                return (
                  <div key={fund.fundId}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-900">{fund.fundName}</p>
                      <p className="text-sm text-gray-600">{utilPct.toFixed(1)}%</p>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all',
                          utilPct > 90
                            ? 'bg-red-500'
                            : utilPct > 75
                              ? 'bg-amber-500'
                              : 'bg-green-500',
                        )}
                        style={{ width: `${Math.min(utilPct, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {fmt(fund.capitalDeployed)} of {fmt(fund.capitalCommitted)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </ComponentErrorBoundary>
      )}

      {/* Repayment Tracker */}
      <ComponentErrorBoundary name="Repayment Tracker">
        <RepaymentTracker />
      </ComponentErrorBoundary>

      {/* Pending Approvals + Upcoming Maturities + Arrears + Diary Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {approvalStats && (
          <ComponentErrorBoundary name="Approvals Card">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Pending Approvals</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {approvalStats.byStatus?.IN_REVIEW ?? 0}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-amber-100" />
              </div>
              <Button
                variant="secondary"
                onClick={() => navigate('/loans')}
                className="mt-3 !w-auto !min-w-0 !min-h-[30px] !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent text-xs font-medium text-blue-600 hover:text-blue-700 block"
              >
                View Approvals <ArrowRight className="w-3 h-3 inline ml-1" />
              </Button>
            </div>
          </ComponentErrorBoundary>
        )}

        {upcomingMaturities && upcomingMaturities.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">Upcoming Maturities (90 days)</p>
            <div className="space-y-2">
              {upcomingMaturities.slice(0, 3).map((loan: any) => (
                <Link
                  key={loan.loanId}
                  to={`/loans/${loan.loanId}`}
                  className="flex items-center justify-between text-xs hover:bg-gray-50 p-1 rounded transition-colors"
                >
                  <span className="font-medium text-gray-900 truncate">{loan.loanNumber}</span>
                  <span className="text-gray-500 text-[10px]">
                    {new Date(loan.maturityDate).toLocaleDateString('en-AU', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {arrearsAging && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-3">Arrears Summary</p>
            <div className="space-y-1 text-xs">
              {(Array.isArray(arrearsAging?.buckets) ? arrearsAging.buckets : [])
                .slice(0, 3)
                .map((bucket: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-gray-700">
                    <span>{bucket.label}</span>
                    <span className="font-medium">{bucket.count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {diaryOverdue && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Overdue Diary</p>
                <p className="text-2xl font-semibold text-red-600">
                  {diaryOverdue.totalOverdueCount}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-100" />
            </div>
          </div>
        )}
      </div>

      {/* Pipeline Funnel */}
      {pipeline && pipeline.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Loan Pipeline</h2>
            <Button
              variant="secondary"
              onClick={() => navigate('/loans/board')}
              className="!w-auto !min-w-0 !min-h-[30px] !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View board <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex items-stretch gap-1">
            {pipeline.map((stage, i) => {
              const maxCount = Math.max(...pipeline.map(p => p.count), 1);
              const pct = Math.max((stage.count / maxCount) * 100, 8);
              const stageConfig = LOAN_STATUS_CONFIG[stage.status as LoanStatus];
              const color = stageConfig?.color ?? '#94a3b8';

              return (
                <Button
                  variant="secondary"
                  key={stage.status}
                  onClick={() => navigate(`/loans?status=${stage.status}`)}
                  className="flex-1 group relative !w-full !min-w-0 !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent !min-h-0 !rounded-none !flex-col !items-stretch !justify-start"
                >
                  <div
                    className="rounded-lg transition-all group-hover:opacity-80 flex flex-col items-center justify-end p-2"
                    style={{
                      backgroundColor: `${color}15`,
                      borderLeft: `3px solid ${color}`,
                      minHeight: `${Math.max(pct * 0.8, 40)}px`,
                    }}
                  >
                    <span className="text-lg font-bold" style={{ color }}>
                      {stage.count}
                    </span>
                    <span className="text-[10px] text-gray-500 text-center leading-tight mt-0.5">
                      {stageConfig?.label ?? stage.status.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[9px] text-gray-400 mt-0.5">
                      {fmt(stage.totalPrincipal)}
                    </span>
                  </div>
                  {i < pipeline.length - 1 && (
                    <ArrowRight className="absolute -right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 z-10" />
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Origination */}
        <ComponentErrorBoundary name="Monthly Origination">
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900">Monthly Origination</h2>
              <span className="text-xs text-gray-400">Last 12 months</span>
            </div>
            <OriginationChart data={origination ?? []} />
          </div>
        </ComponentErrorBoundary>

        {/* Delinquency breakdown */}
        <ComponentErrorBoundary name="Delinquency Breakdown">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Delinquency Breakdown</h2>
            {delinquency ? (
              <DelinquencyBreakdown
                buckets={delinquency.buckets}
                totalAtRisk={delinquency.totalAtRisk}
                totalAtRiskAmount={delinquency.totalAtRiskAmount}
              />
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">Loading…</p>
            )}
          </div>
        </ComponentErrorBoundary>
      </div>

      {/* Status + Type charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Status Distribution</h2>
            <Button
              variant="secondary"
              onClick={() => navigate('/loans/board')}
              className="!w-auto !min-w-0 !min-h-[30px] !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View board <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
          {statusChartData.length > 0 ? (
            <MiniBarChart data={statusChartData} />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No loans yet</p>
          )}
        </div>

        {/* Type Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Loan Types</h2>
          {typeChartData.length > 0 ? (
            <div className="space-y-4">
              <MiniBarChart data={typeChartData} />
              <div className="grid grid-cols-2 gap-2">
                {(typeBreakdown ?? []).map(t => (
                  <div key={t.loanType} className="flex items-center justify-between text-xs py-1">
                    <span className="text-gray-600">{t.loanType.replace(/_/g, ' ')}</span>
                    <span className="text-gray-400">{fmtRate(t.avgRate)} avg</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No loans yet</p>
          )}
        </div>
      </div>

      {/* Top Borrowers */}
      <ComponentErrorBoundary name="Top Borrowers">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Top Borrowers</h2>
            <Button
              variant="secondary"
              onClick={() => navigate('/borrowers')}
              className="!w-auto !min-w-0 !min-h-[30px] !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View all <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
          {(topBorrowers ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No borrowers yet</p>
          ) : (
            <>
              <DataTable<(typeof displayedBorrowers)[0]>
                columns={[
                  {
                    key: 'name',
                    header: 'Borrower',
                    render: b => (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-semibold shrink-0">
                          {displayedBorrowers.indexOf(b) + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{b.name}</p>
                          <p className="text-[11px] text-gray-400">{b.email}</p>
                        </div>
                      </div>
                    ),
                  },
                  { key: 'loanCount', header: 'Loans', align: 'right' },
                  {
                    key: 'activeLoans',
                    header: 'Active',
                    align: 'right',
                    render: b => (
                      <span
                        className={cn(
                          'font-medium',
                          b.activeLoans > 0 ? 'text-green-600' : 'text-gray-400',
                        )}
                      >
                        {b.activeLoans}
                      </span>
                    ),
                  },
                  {
                    key: 'totalPrincipal',
                    header: 'Total Exposure',
                    align: 'right',
                    render: b => fmt(b.totalPrincipal),
                  },
                ]}
                data={displayedBorrowers}
                keyField="id"
                emptyMessage="No borrowers yet"
                onRowClick={b => navigate(`/borrowers/${b.id}`)}
              />
              {(topBorrowers ?? []).length > 5 && (
                <Button
                  variant="secondary"
                  onClick={() => setShowAllBorrowers(!showAllBorrowers)}
                  className="mt-3 mx-auto !w-auto !min-w-0 !min-h-[30px] !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  {showAllBorrowers ? (
                    <>
                      Show less <ChevronUp className="w-3 h-3" />
                    </>
                  ) : (
                    <>
                      Show all {topBorrowers?.length} <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </ComponentErrorBoundary>

      {/* Recently Viewed + Activity — side by side on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recently Viewed Loans */}
        {recentLoans.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-900">Recently Viewed</h2>
              </div>
            </div>
            <div className="space-y-1">
              {recentLoans.slice(0, 6).map(loan => {
                const cfg = LOAN_STATUS_CONFIG[loan.status as LoanStatus];
                return (
                  <Link
                    key={loan.id}
                    to={`/loans/${loan.id}`}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                      <FileText className="w-3.5 h-3.5 text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 truncate">
                        <span className="font-mono font-medium">{loan.loanNumber}</span>
                        <span className="text-gray-400 ml-1.5">· {loan.borrowerName}</span>
                      </p>
                    </div>
                    {cfg && (
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }}
                      >
                        {cfg.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Activity Feed */}
        <div
          className={cn(
            'bg-white rounded-lg border border-gray-200 p-5',
            recentLoans.length === 0 && 'lg:col-span-2',
          )}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <Button
              variant="secondary"
              onClick={() => navigate('/activity')}
              className="!w-auto !min-w-0 !min-h-[30px] !px-0 !py-0 !border-0 !bg-transparent hover:!bg-transparent text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View all <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
          {!recentActivity?.data || recentActivity.data.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No recent activity</p>
          ) : (
            <div className="space-y-1">
              {recentActivity.data.map((entry: any) => {
                const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
                  'comment.added': <MessageSquare className="w-3.5 h-3.5 text-blue-500" />,
                  'assignment.created': <UserPlus className="w-3.5 h-3.5 text-green-500" />,
                  'status.changed': <ArrowRightLeft className="w-3.5 h-3.5 text-purple-500" />,
                  'payment.received': <DollarSign className="w-3.5 h-3.5 text-emerald-500" />,
                  'loan.created': <FileText className="w-3.5 h-3.5 text-indigo-500" />,
                  'schedule.generated': <Spinner size="sm" className="w-3.5 h-3.5 text-cyan-500" />,
                };
                const icon = ACTIVITY_ICONS[entry.actionType] ?? (
                  <Activity className="w-3.5 h-3.5 text-gray-400" />
                );
                const actorName = entry.actor
                  ? [entry.actor.firstName, entry.actor.lastName].filter(Boolean).join(' ') ||
                    'User'
                  : 'System';
                const timeStr = fmtTimeAgo(entry.createdAt);
                const actionLabel =
                  typeof entry.actionType === 'string' && entry.actionType.length > 0
                    ? entry.actionType
                        .split('.')
                        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')
                    : 'Updated';

                return (
                  <div
                    key={entry.id}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 truncate">
                        <span className="font-medium">{actorName}</span>{' '}
                        <span className="text-gray-400">{actionLabel}</span>
                        {entry.loan && (
                          <>
                            {' on '}
                            <Link
                              to={`/loans/${entry.loan.id}`}
                              className="text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              {entry.loan.loanNumber}
                            </Link>
                          </>
                        )}
                      </p>
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0">{timeStr}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* end grid */}
    </PageLayout>
  );
}
